import { useEffect, useState } from "react";
import { supabase } from "../../lib/supabase";
import { ConfirmModal } from "../../components/ConfirmModal";
import { Star, Eye, EyeOff, Trash2, MessageSquare } from "lucide-react";

/* ---------- TYPE ---------- */

interface Review {
  id: string;
  rating: number;
  comment: string;
  image_url: string | null;
  is_hidden: boolean;
  created_at: string;
  admin_reply: string | null;
  replied_at: string | null;
  profiles?: {
    full_name: string | null;
  };
}

/* ---------- COMPONENT ---------- */

export function ReviewsManagementTab() {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [replyContent, setReplyContent] = useState("");
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  useEffect(() => {
    loadReviews();
  }, []);

  async function loadReviews() {
    const { data, error } = await supabase
  .from("reviews")
  .select(`
    *,
    profiles:profiles!reviews_user_id_fkey (
      full_name
    )
  `)
  .order("created_at", { ascending: false });

    if (error) {
      console.error(error);
      return;
    }

    setReviews(data || []);
    setLoading(false);
  }

  /* ---------- HIDE / UNHIDE ---------- */

  async function toggleVisibility(id: string, current: boolean) {
    const { error } = await supabase
      .from("reviews")
      .update({ is_hidden: !current })
      .eq("id", id);

    if (!error) {
      setReviews((prev) =>
        prev.map((r) =>
          r.id === id ? { ...r, is_hidden: !current } : r
        )
      );
    }
  }

  /* ---------- DELETE ---------- */

  async function handleDelete(id: string) {
    const { error } = await supabase
      .from("reviews")
      .delete()
      .eq("id", id);

    if (!error) {
      setReviews((prev) => prev.filter((r) => r.id !== id));
    }

    setDeleteConfirmId(null);
  }

  /* ---------- REPLY ---------- */

  async function handleReply(id: string) {
    if (!replyContent.trim()) return;

    const { data: userData } = await supabase.auth.getUser();
    const adminId = userData.user?.id;

    const { error } = await supabase
      .from("reviews")
      .update({
        admin_reply: replyContent,
        replied_at: new Date().toISOString(),
        replied_by: adminId,
      })
      .eq("id", id);

    if (!error) {
      setReviews((prev) =>
        prev.map((r) =>
          r.id === id
            ? {
                ...r,
                admin_reply: replyContent,
                replied_at: new Date().toISOString(),
              }
            : r
        )
      );
    }

    setReplyContent("");
    setReplyingTo(null);
  }

  function formatDate(date: string) {
    return new Date(date).toLocaleString("en-GB", {
      day: "2-digit",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  if (loading) return <div>Loading reviews...</div>;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold text-gray-900">
        Reviews Management
      </h1>

      {reviews.map((review) => (
        <div
          key={review.id}
          className={`bg-white shadow rounded-xl p-6 ${
            review.is_hidden ? "opacity-60 bg-gray-50" : ""
          }`}
        >
          <div className="flex justify-between items-start">
            <div className="flex-1">

              {/* Rating + Name */}
              <div className="flex items-center mb-2">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    className={`h-5 w-5 ${
                      i < review.rating
                        ? "text-yellow-400 fill-current"
                        : "text-gray-300"
                    }`}
                  />
                ))}

                <span className="ml-2 font-semibold text-gray-900">
                  {review.profiles?.full_name || "No Name"}
                </span>

                {review.is_hidden && (
                  <span className="ml-3 text-xs bg-gray-200 px-2 py-1 rounded-full">
                    Hidden
                  </span>
                )}
              </div>

              {/* Comment */}
              <p className="text-gray-800">{review.comment}</p>

              {/* Image */}
              {review.image_url && (
                <img
                  src={review.image_url}
                  className="mt-3 w-32 rounded-lg border"
                />
              )}

              {/* Created timestamp */}
              <p className="text-xs text-gray-500 mt-3">
                Posted: {formatDate(review.created_at)}
              </p>

              {/* Admin Reply */}
              {review.admin_reply && (
                <div className="mt-4 pl-4 border-l-4 border-nhs-blue">
                  <p className="text-sm font-semibold text-nhs-blue">
                    Talis'on Services
                  </p>
                  <p className="text-sm text-gray-700">
                    {review.admin_reply}
                  </p>
                  {review.replied_at && (
                    <p className="text-xs text-gray-500 mt-1">
                      Replied: {formatDate(review.replied_at)}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex space-x-3 ml-4">
              <button
                onClick={() =>
                  toggleVisibility(review.id, review.is_hidden)
                }
                className="text-gray-500 hover:text-nhs-blue"
              >
                {review.is_hidden ? (
                  <Eye className="h-5 w-5" />
                ) : (
                  <EyeOff className="h-5 w-5" />
                )}
              </button>

              <button
                onClick={() => setDeleteConfirmId(review.id)}
                className="text-gray-500 hover:text-red-600"
              >
                <Trash2 className="h-5 w-5" />
              </button>

              <button
                onClick={() => setReplyingTo(review.id)}
                className="text-gray-500 hover:text-nhs-blue"
              >
                <MessageSquare className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Reply Form */}
          {replyingTo === review.id && (
            <div className="mt-4">
              <textarea
                rows={3}
                value={replyContent}
                onChange={(e) => setReplyContent(e.target.value)}
                className="w-full border rounded-lg p-2 text-sm"
                placeholder="Write a reply..."
              />
              <div className="mt-2 flex justify-end space-x-3">
                <button
                  onClick={() => setReplyingTo(null)}
                  className="text-sm text-gray-500"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleReply(review.id)}
                  className="bg-nhs-blue text-white px-4 py-1 rounded-lg text-sm"
                >
                  Reply
                </button>
              </div>
            </div>
          )}
        </div>
      ))}

      <ConfirmModal
        open={deleteConfirmId !== null}
        title="Delete Review"
        message="Are you sure you want to delete this review?"
        variant="danger"
        confirmText="Delete"
        onConfirm={() =>
          deleteConfirmId && handleDelete(deleteConfirmId)
        }
        onCancel={() => setDeleteConfirmId(null)}
      />
    </div>
  );
}